/**
 * CursorGame custom scrollbars — vertical + horizontal, synced, touch + mouse.
 * Bars are children of the scroll host, so their offsets are pinned to scrollTop/Left
 * so the track stays glued to the visible viewport (like a native scrollbar).
 */

type Axis = "y" | "x";

type ScrollbarBinding = {
    host: HTMLElement;
    barY: HTMLElement | null;
    thumbY: HTMLElement | null;
    barX: HTMLElement | null;
    thumbX: HTMLElement | null;
    ro: ResizeObserver;
};

const bindings = new WeakMap<HTMLElement, ScrollbarBinding>();
let mounted = false;
let scanTimer: ReturnType<typeof setTimeout> | null = null;
let scrollHideTimer: ReturnType<typeof setTimeout> | null = null;

const ACTIVE_CLASS = "cg-scroll-active";
const HOST_CLASS = "cg-scroll-host";
const INSET = 6;
const BAR_THICK = 7;
const BAR_EDGE = 5;
const MIN_THUMB = 28;
/** ~line height used by browsers for DOM_DELTA_LINE wheel events. */
const WHEEL_LINE_PX = 40;
/** Soften vertical→horizontal remapping so a mouse notch feels like normal page scroll. */
const WHEEL_X_FROM_Y_SCALE = 0.65;

function overflowYScrollable(el: HTMLElement): boolean {
    const oy = getComputedStyle(el).overflowY;
    return oy === "auto" || oy === "scroll" || oy === "overlay";
}

function overflowXScrollable(el: HTMLElement): boolean {
    const ox = getComputedStyle(el).overflowX;
    return ox === "auto" || ox === "scroll" || ox === "overlay";
}

function shouldSkipHost(el: HTMLElement): boolean {
    if (el.dataset.cgScroll === "off") return true;
    if (el.classList.contains("cg-scrollbar")) return true;
    if (el.closest(".cg-scrollbar")) return true;
    if (el.tagName === "TEXTAREA") return true;
    return false;
}

function maxScrollY(host: HTMLElement): number {
    return Math.max(0, host.scrollHeight - host.clientHeight);
}

function maxScrollX(host: HTMLElement): number {
    return Math.max(0, host.scrollWidth - host.clientWidth);
}

/** Keep the Y track fixed in the visible viewport while the host scrolls. */
function pinBarY(host: HTMLElement, bar: HTMLElement, hasX: boolean): void {
    const xReserve = hasX ? BAR_THICK + BAR_EDGE : 0;
    const trackH = Math.max(0, host.clientHeight - INSET * 2 - xReserve);
    bar.style.top = `${host.scrollTop + INSET}px`;
    bar.style.left = `${host.scrollLeft + host.clientWidth - BAR_THICK - BAR_EDGE}px`;
    bar.style.right = "auto";
    bar.style.bottom = "auto";
    bar.style.width = `${BAR_THICK}px`;
    bar.style.height = `${trackH}px`;
}

/** Keep the X track fixed in the visible viewport while the host scrolls. */
function pinBarX(host: HTMLElement, bar: HTMLElement, hasY: boolean): void {
    const yReserve = hasY ? BAR_THICK + BAR_EDGE : 0;
    const trackW = Math.max(0, host.clientWidth - INSET * 2 - yReserve);
    bar.style.left = `${host.scrollLeft + INSET}px`;
    bar.style.top = `${host.scrollTop + host.clientHeight - BAR_THICK - BAR_EDGE}px`;
    bar.style.right = "auto";
    bar.style.bottom = "auto";
    bar.style.height = `${BAR_THICK}px`;
    bar.style.width = `${trackW}px`;
}

function syncThumbY(host: HTMLElement, thumb: HTMLElement, bar: HTMLElement, hasX: boolean): void {
    const maxScroll = maxScrollY(host);
    const ch = host.clientHeight;
    const sh = host.scrollHeight;
    pinBarY(host, bar, hasX);
    if (maxScroll <= 0.5 || ch < 24) {
        bar.classList.add("hidden");
        return;
    }
    bar.classList.remove("hidden");
    const track = Math.max(1, bar.clientHeight || Number.parseFloat(bar.style.height) || 1);
    const thumbH = Math.min(track, Math.max(MIN_THUMB, (ch / Math.max(sh, 1)) * track));
    const maxThumbTop = Math.max(0, track - thumbH);
    const ratio = maxScroll > 0 ? host.scrollTop / maxScroll : 0;
    // Clamp so the thumb always stays fully inside the track, including at scroll end.
    const top = Math.min(maxThumbTop, Math.max(0, ratio * maxThumbTop));
    thumb.style.height = `${thumbH}px`;
    thumb.style.transform = `translateY(${top}px)`;
    thumb.dataset.cgThumbSize = String(thumbH);
    thumb.dataset.cgTrackTravel = String(maxThumbTop);
}

function syncThumbX(host: HTMLElement, thumb: HTMLElement, bar: HTMLElement, hasY: boolean): void {
    const maxScroll = maxScrollX(host);
    const cw = host.clientWidth;
    const sw = host.scrollWidth;
    pinBarX(host, bar, hasY);
    if (maxScroll <= 0.5 || cw < 24) {
        bar.classList.add("hidden");
        return;
    }
    bar.classList.remove("hidden");
    const track = Math.max(1, bar.clientWidth || Number.parseFloat(bar.style.width) || 1);
    const thumbW = Math.min(track, Math.max(MIN_THUMB, (cw / Math.max(sw, 1)) * track));
    const maxThumbLeft = Math.max(0, track - thumbW);
    const ratio = maxScroll > 0 ? host.scrollLeft / maxScroll : 0;
    const left = Math.min(maxThumbLeft, Math.max(0, ratio * maxThumbLeft));
    thumb.style.width = `${thumbW}px`;
    thumb.style.transform = `translateX(${left}px)`;
    thumb.dataset.cgThumbSize = String(thumbW);
    thumb.dataset.cgTrackTravel = String(maxThumbLeft);
}

function syncBinding(b: ScrollbarBinding): void {
    const hasY = !!(b.barY && b.thumbY);
    const hasX = !!(b.barX && b.thumbX);
    if (b.barY && b.thumbY) syncThumbY(b.host, b.thumbY, b.barY, hasX);
    if (b.barX && b.thumbX) syncThumbX(b.host, b.thumbX, b.barX, hasY);
}

function markScrollActive(host: HTMLElement): void {
    host.classList.add(ACTIVE_CLASS);
    if (scrollHideTimer) clearTimeout(scrollHideTimer);
    scrollHideTimer = setTimeout(() => {
        scrollHideTimer = null;
        document.querySelectorAll(`.${HOST_CLASS}.${ACTIVE_CLASS}`).forEach((el) => {
            if (el instanceof HTMLElement && !el.matches(":hover")) {
                el.classList.remove(ACTIVE_CLASS);
            }
        });
    }, 900);
}

function clampScroll(host: HTMLElement, axis: Axis, value: number): void {
    if (axis === "y") {
        const max = maxScrollY(host);
        host.scrollTop = Math.min(max, Math.max(0, value));
    } else {
        const max = maxScrollX(host);
        host.scrollLeft = Math.min(max, Math.max(0, value));
    }
}

/** Convert a WheelEvent delta into CSS pixels (browser-like). */
function wheelDeltaPx(e: WheelEvent, host: HTMLElement, preferAxis: Axis): number {
    const raw = preferAxis === "x" ? (e.deltaX !== 0 ? e.deltaX : e.deltaY) : e.deltaY;
    const fromVerticalWheel = preferAxis === "x" && e.deltaX === 0 && e.deltaY !== 0;
    let px = raw;
    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        px = raw * WHEEL_LINE_PX;
    } else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        px = raw * (preferAxis === "x" ? host.clientWidth : host.clientHeight);
    }
    if (fromVerticalWheel) px *= WHEEL_X_FROM_Y_SCALE;
    return px;
}

function thumbTravelPx(thumb: HTMLElement, bar: HTMLElement, axis: Axis): number {
    const stored = Number(thumb.dataset.cgTrackTravel);
    if (Number.isFinite(stored) && stored >= 0) return Math.max(1, stored);
    const size =
        Number(thumb.dataset.cgThumbSize) ||
        (axis === "y" ? thumb.offsetHeight : thumb.offsetWidth) ||
        MIN_THUMB;
    const barSize = axis === "y" ? bar.clientHeight : bar.clientWidth;
    return Math.max(1, barSize - size);
}

function wireThumbDrag(
    host: HTMLElement,
    bar: HTMLElement,
    thumb: HTMLElement,
    axis: Axis,
): void {
    let dragPointerId = -1;
    let dragStartClient = 0;
    let dragStartScroll = 0;
    let dragTravel = 1;

    thumb.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragPointerId = e.pointerId;
        dragStartClient = axis === "y" ? e.clientY : e.clientX;
        dragStartScroll = axis === "y" ? host.scrollTop : host.scrollLeft;
        // Refresh metrics so travel matches current track size.
        const b = bindings.get(host);
        if (b) syncBinding(b);
        dragTravel = thumbTravelPx(thumb, bar, axis);
        thumb.setPointerCapture(e.pointerId);
        host.classList.add(ACTIVE_CLASS);
    });

    thumb.addEventListener("pointermove", (e) => {
        if (e.pointerId !== dragPointerId) return;
        e.preventDefault();
        const maxScroll = axis === "y" ? maxScrollY(host) : maxScrollX(host);
        const delta = (axis === "y" ? e.clientY : e.clientX) - dragStartClient;
        const scrollDelta = (delta / Math.max(1, dragTravel)) * maxScroll;
        clampScroll(host, axis, dragStartScroll + scrollDelta);
    });

    const endDrag = (e: PointerEvent) => {
        if (e.pointerId !== dragPointerId) return;
        dragPointerId = -1;
        try {
            thumb.releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    };
    thumb.addEventListener("pointerup", endDrag);
    thumb.addEventListener("pointercancel", endDrag);

    bar.addEventListener("pointerdown", (e) => {
        if (e.target !== bar) return;
        e.preventDefault();
        const b = bindings.get(host);
        if (b) syncBinding(b);
        const rect = bar.getBoundingClientRect();
        const maxScroll = axis === "y" ? maxScrollY(host) : maxScrollX(host);
        const travel = thumbTravelPx(thumb, bar, axis);
        if (axis === "y") {
            const thumbH = Number(thumb.dataset.cgThumbSize) || thumb.offsetHeight || MIN_THUMB;
            const y = e.clientY - rect.top - thumbH / 2;
            clampScroll(host, "y", (y / Math.max(1, travel)) * maxScroll);
        } else {
            const thumbW = Number(thumb.dataset.cgThumbSize) || thumb.offsetWidth || MIN_THUMB;
            const x = e.clientX - rect.left - thumbW / 2;
            clampScroll(host, "x", (x / Math.max(1, travel)) * maxScroll);
        }
        markScrollActive(host);
    });
}

function attachScrollbar(host: HTMLElement): void {
    if (bindings.has(host) || shouldSkipHost(host)) return;
    const needY = overflowYScrollable(host);
    const needX = overflowXScrollable(host);
    if (!needY && !needX) return;

    host.classList.add(HOST_CLASS);
    if (getComputedStyle(host).position === "static") {
        host.style.position = "relative";
    }

    let barY: HTMLElement | null = null;
    let thumbY: HTMLElement | null = null;
    let barX: HTMLElement | null = null;
    let thumbX: HTMLElement | null = null;

    if (needY) {
        barY = document.createElement("div");
        barY.className = "cg-scrollbar cg-scrollbar--y";
        barY.setAttribute("aria-hidden", "true");
        thumbY = document.createElement("div");
        thumbY.className = "cg-scrollbar-thumb";
        barY.appendChild(thumbY);
        host.appendChild(barY);
        wireThumbDrag(host, barY, thumbY, "y");
    }

    if (needX) {
        barX = document.createElement("div");
        barX.className = "cg-scrollbar cg-scrollbar--x";
        barX.setAttribute("aria-hidden", "true");
        thumbX = document.createElement("div");
        thumbX.className = "cg-scrollbar-thumb";
        barX.appendChild(thumbX);
        host.appendChild(barX);
        wireThumbDrag(host, barX, thumbX, "x");
    }

    const onScroll = () => {
        const b = bindings.get(host);
        if (b) syncBinding(b);
        markScrollActive(host);
    };
    host.addEventListener("scroll", onScroll, { passive: true });

    host.addEventListener("pointerenter", () => host.classList.add(ACTIVE_CLASS));
    host.addEventListener("pointerleave", () => {
        host.classList.remove(ACTIVE_CLASS);
    });

    // Vertical-wheel → horizontal scroll for x-only hubs (PLAY category strip).
    if (needX && !needY) {
        host.addEventListener(
            "wheel",
            (e) => {
                if (e.ctrlKey) return;
                const max = maxScrollX(host);
                if (max <= 0) return;
                const dx = wheelDeltaPx(e, host, "x");
                if (dx === 0) return;
                e.preventDefault();
                clampScroll(host, "x", host.scrollLeft + dx);
                markScrollActive(host);
            },
            { passive: false },
        );
    }

    const ro = new ResizeObserver(() => {
        const b = bindings.get(host);
        if (b) syncBinding(b);
    });
    ro.observe(host);
    if (host.firstElementChild instanceof HTMLElement) {
        ro.observe(host.firstElementChild);
    }

    const binding: ScrollbarBinding = { host, barY, thumbY, barX, thumbX, ro };
    bindings.set(host, binding);
    syncBinding(binding);
}

function collectScrollableHosts(root: ParentNode = document.body): HTMLElement[] {
    const out: HTMLElement[] = [];
    const consider = (el: HTMLElement) => {
        if (shouldSkipHost(el)) return;
        if (overflowYScrollable(el) || overflowXScrollable(el)) out.push(el);
    };
    if (root instanceof HTMLElement) consider(root);
    root.querySelectorAll<HTMLElement>("*").forEach(consider);
    return out;
}

export function refreshCustomScrollbars(): void {
    for (const host of collectScrollableHosts()) {
        if (!bindings.has(host)) attachScrollbar(host);
        else {
            const binding = bindings.get(host);
            if (binding) syncBinding(binding);
        }
    }
}

function scheduleScan(): void {
    if (scanTimer) return;
    scanTimer = setTimeout(() => {
        scanTimer = null;
        refreshCustomScrollbars();
    }, 60);
}

export function mountCustomScrollbars(): void {
    if (mounted) return;
    mounted = true;

    refreshCustomScrollbars();

    window.addEventListener("resize", scheduleScan, { passive: true });

    const mo = new MutationObserver((records) => {
        for (const rec of records) {
            if (rec.type === "attributes" && rec.target instanceof HTMLElement) {
                if (rec.attributeName === "class" || rec.attributeName === "hidden") {
                    scheduleScan();
                    return;
                }
            }
            if (rec.type === "childList") {
                scheduleScan();
                return;
            }
        }
    });
    mo.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["class", "hidden", "style"],
    });
}
